import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Trash2, UserPlus, Loader2, X, Camera, Eye, EyeOff, Check, 
  MapPin, Phone, Mail, Award, Building2, Calendar, Pencil,
  ChevronRight, ChevronLeft, User, ShieldCheck, Briefcase, AlertCircle,
  Activity, Star, Globe, Smartphone, UserCheck
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const PractitionerSetup = () => {
  const [practitioners, setPractitioners] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  
  const initialForm = {
    firstName: '', lastName: '', email: '', password: '', confirmPassword: '',
    phone: '', country: '', countryCode: '', dateOfBirth: '', gender: '',
    isActive: true, specialization: '', city: '', registrationNumber: '',
    address: '', clinicId: '', department: '', role: 'doctor'
  };

  const [form, setForm] = useState(initialForm);

  const API_URL = `${window.location.protocol}//${window.location.hostname}/api/users`;
  const CLINICS_API_URL = `${window.location.protocol}//${window.location.hostname}/api/clinics`;
  const BASE_URL = `${window.location.protocol}//${window.location.hostname}`;
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, clinicsRes] = await Promise.all([
        axios.get(API_URL, { headers }),
        axios.get(CLINICS_API_URL, { headers })
      ]);
      setPractitioners(usersRes.data.filter(u => u.role !== 'patient'));
      setClinics(clinicsRes.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    if (step === 1) {
      if (!form.firstName) newErrors.firstName = 'Required';
      if (!form.lastName) newErrors.lastName = 'Required';
    } else if (step === 2) {
      if (!form.clinicId) newErrors.clinicId = 'Select Clinic';
    } else if (step === 3) {
      if (!form.email) newErrors.email = 'Required';
      if (!editingId) {
        if (!form.password) newErrors.password = 'Required';
        if (form.password !== form.confirmPassword) newErrors.confirmPassword = 'Mismatch';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const openAddModal = () => {
    setEditingId(null);
    setForm(initialForm);
    setErrors({});
    setImageFile(null);
    setImagePreview(null);
    setCurrentStep(1);
    setShowModal(true);
  };

  const openEditModal = (p) => {
    setEditingId(p.id);
    const dob = p.dateOfBirth ? p.dateOfBirth.split('T')[0] : '';
    setForm({ ...p, dateOfBirth: dob, password: '', confirmPassword: '' });
    setErrors({});
    setImageFile(null);
    setImagePreview(p.profilePhoto ? `${BASE_URL}${p.profilePhoto}` : null);
    setCurrentStep(1);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(currentStep)) return;
    if (currentStep < 3) { setCurrentStep(currentStep + 1); return; }
    
    const toastId = toast.loading(editingId ? 'Updating profile...' : 'Creating profile...');
    try {
      const formData = new FormData();
      Object.keys(form).forEach(key => {
          if (form[key] !== null && form[key] !== undefined) {
              formData.append(key, form[key]);
          }
      });
      if (imageFile) formData.append('profilePhoto', imageFile);

      if (editingId) {
        await axios.put(`${API_URL}/${editingId}`, formData, { 
            headers: { ...headers, 'Content-Type': 'multipart/form-data' } 
        });
        toast.success('Practitioner updated!', { id: toastId });
      } else {
        await axios.post(API_URL, formData, { 
            headers: { ...headers, 'Content-Type': 'multipart/form-data' } 
        });
        toast.success('Practitioner registered!', { id: toastId });
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      if (err.response?.status === 409) {
          setErrors({ email: 'Already Exists' });
          toast.error('Email already exists', { id: toastId });
      } else {
          toast.error('Error: ' + (err.response?.data?.message || err.message), { id: toastId });
      }
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) return;
    const toastId = toast.loading('Deleting staff...');
    try { 
        await axios.delete(`${API_URL}/${id}`, { headers }); 
        toast.success('Staff deleted successfully', { id: toastId });
        fetchData(); 
    } catch (err) { 
        toast.error('Failed to delete staff', { id: toastId }); 
    }
  };

  const steps = [
    { id: 1, title: 'Identity', icon: <User className="w-4 h-4" /> },
    { id: 2, title: 'Professional', icon: <Briefcase className="w-4 h-4" /> },
    { id: 3, title: 'Security', icon: <ShieldCheck className="w-4 h-4" /> }
  ];

  if (loading && practitioners.length === 0) return (
    <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
  );

  return (
    <div className="p-4 bg-[#F8FAFC] min-h-screen font-sans text-slate-700">
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="bg-white/90 backdrop-blur-md px-6 py-4 rounded-2xl border border-slate-200 shadow-xl shadow-slate-200/20 flex justify-between items-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-indigo-500 via-sky-500 to-indigo-500" />
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-200">
                    <UserPlus className="w-5 h-5 text-white" />
                </div>
                <div>
                    <h1 className="text-xl font-black text-slate-800 tracking-tight leading-none">Practitioner Setup</h1>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Hospital medical staff registry</p>
                </div>
            </div>
            <button onClick={openAddModal} className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-black text-white rounded-xl text-[11px] font-black uppercase tracking-widest shadow-lg shadow-indigo-100 transition-all active:scale-95 group">
                <UserPlus className="w-3.5 h-3.5 transition-transform group-hover:scale-110" />
                Add Staff
            </button>
        </div>

        <div className="bg-white rounded-[24px] border border-slate-200 shadow-sm overflow-hidden text-[11px]">
          <div className="overflow-x-auto custom-scrollbar">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/50 text-left">
                <tr>
                  <th className="px-6 py-3 font-black text-slate-400 uppercase tracking-widest">Identify / Role</th>
                  <th className="px-6 py-3 font-black text-slate-400 uppercase tracking-widest text-center">Location</th>
                  <th className="px-6 py-3 font-black text-slate-400 uppercase tracking-widest">Specialization</th>
                  <th className="px-6 py-3 font-black text-slate-400 uppercase tracking-widest">Connect</th>
                  <th className="px-6 py-3 text-center font-black text-slate-400 uppercase tracking-widest">Status</th>
                  <th className="px-6 py-3 text-right font-black text-slate-400 uppercase tracking-widest">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {practitioners.map(p => (
                  <tr key={p.id} className="hover:bg-indigo-50/10 transition-all group">
                    <td className="px-6 py-2.5 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        {p.profilePhoto ? (
                             <img src={`${BASE_URL}${p.profilePhoto}`} className="w-8 h-8 rounded-lg object-cover border border-slate-200 group-hover:border-indigo-400" alt="" />
                        ) : (
                            <div className="w-8 h-8 bg-white border border-slate-200 rounded-lg flex items-center justify-center text-indigo-600 font-black text-[11px] group-hover:border-indigo-400">
                                {p.firstName ? p.firstName[0] : 'U'}
                            </div>
                        )}
                        <div>
                          <div className="text-slate-800 font-bold text-[13px] leading-tight tracking-tight uppercase">{p.firstName} {p.lastName}</div>
                          <div className="text-[9px] text-slate-400 font-black uppercase tracking-widest flex items-center gap-1 mt-0.5"><ShieldCheck className="w-3 h-3 text-emerald-500" />{p.role}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-2.5">
                      <div className="flex justify-center">
                        <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-slate-50 border border-slate-100 rounded-lg font-bold text-slate-500 text-[10px]">
                            <Building2 className="w-3 h-3 text-slate-400" />
                            {clinics.find(c => c.id === Number(p.clinicId))?.name || 'Main medical'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-2.5 whitespace-nowrap"><div className="flex items-center gap-2"><Activity className="w-3 h-3 text-indigo-400" /><div className="font-bold text-slate-700">{p.specialization || 'Review'}</div></div></td>
                    <td className="px-6 py-2.5"><div className="text-slate-500 font-bold max-w-[120px] truncate">{p.email}</div><div className="text-[9px] text-slate-400 font-black uppercase tracking-wider">{p.phone || 'No phone'}</div></td>
                    <td className="px-6 py-2.5 text-center whitespace-nowrap"><span className={`px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest border ${p.isActive ? 'bg-emerald-50 text-emerald-600 border-emerald-100 shadow-sm' : 'bg-slate-50 text-slate-400 border-slate-100'}`}>{p.isActive ? 'Verified' : 'Offline'}</span></td>
                    <td className="px-6 py-2.5 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => openEditModal(p)} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-white rounded-lg transition-all"><Pencil className="w-3.5 h-3.5" /></button>
                          <button onClick={() => deleteUser(p.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-white rounded-lg transition-all"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4 overflow-hidden">
          <div className="bg-white rounded-[32px] w-full max-w-xl shadow-2xl relative overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-100">
            <div className="px-8 py-4 border-b border-slate-50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-600 border border-indigo-100 shrink-0">{steps[currentStep-1].icon}</div>
                  <div><h2 className="text-[17px] font-black text-slate-800 tracking-tight leading-none uppercase">{editingId ? 'Edit Profile' : 'New Practitioner'}</h2><p className="text-[9px] text-slate-400 font-black uppercase tracking-widest mt-1">Step {currentStep} of 3 • {steps[currentStep-1].title}</p></div>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-all text-slate-400 hover:text-slate-900"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-8 py-3 bg-slate-50/50 flex items-center justify-between gap-4 border-b border-slate-50">
               {steps.map(s => (<div key={s.id} className={`flex-1 h-1.5 rounded-full transition-all duration-500 ${currentStep >= s.id ? 'bg-indigo-600 shadow-[0_0_8px_rgba(79,70,229,0.3)]' : 'bg-slate-200'}`} />))}
            </div>
            <form onSubmit={handleSubmit} className="px-8 py-5 space-y-4 max-h-[70vh] overflow-hidden">
              {currentStep === 1 && (
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 animate-in fade-in duration-300">
                  <div className="col-span-2 flex items-center gap-4 py-2 px-3 bg-indigo-50/20 rounded-2xl border border-indigo-100/20 mb-1">
                    <div 
                        onClick={() => fileInputRef.current?.click()}
                        className="w-12 h-12 bg-white rounded-xl border-2 border-dashed border-indigo-200 flex items-center justify-center text-indigo-400 cursor-pointer hover:border-indigo-400 transition-all shrink-0 overflow-hidden"
                    >
                      {imagePreview ? (
                          <img src={imagePreview} className="w-full h-full object-cover" alt="" />
                      ) : (
                          <Camera className="w-5 h-5" />
                      )}
                    </div>
                    <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageChange} />
                    <div><h4 className="text-[14px] font-black text-indigo-900 leading-tight uppercase tracking-tight">Identity Details</h4><p className="text-[9px] text-indigo-600/60 font-bold uppercase tracking-widest">Phase 1: Practitioner Labels</p></div>
                  </div>
                  <InputField label="First Name" name="firstName" value={form.firstName} onChange={handleChange} placeholder="Liam..." error={errors.firstName} required />
                  <InputField label="Last Name" name="lastName" value={form.lastName} onChange={handleChange} placeholder="Smith..." error={errors.lastName} required />
                  <InputField label="Birth Day" name="dateOfBirth" type="date" value={form.dateOfBirth} onChange={handleChange} />
                  <div className="space-y-1"><label className="text-[9px] font-black text-slate-400 uppercase tracking-widest px-0.5">Gender Selection</label><div className="flex gap-1.5">{['male', 'female'].map(g => (<button key={g} type="button" onClick={() => setForm({...form, gender: g})} className={`flex-1 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest border transition-all ${form.gender === g ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm' : 'bg-white border-slate-100 text-slate-400'}`}>{g}</button>))}</div></div>
                  <div className="col-span-2"><InputField label="Location / Street Address" name="address" value={form.address} onChange={handleChange} placeholder="Branch A, Area 51..." /></div>
                </div>
              )}
              {currentStep === 2 && (
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 animate-in fade-in duration-300">
                  <InputField label="Reg Number" name="registrationNumber" value={form.registrationNumber} onChange={handleChange} placeholder="Medical License ID..." /><InputField label="Specialty" name="specialization" value={form.specialization} onChange={handleChange} placeholder="Neuro Surgeon..." />
                  <div className="col-span-2"><SelectField label="Medical Facility" name="clinicId" value={form.clinicId} onChange={handleChange} options={clinics.map(c => ({ value: c.id, label: c.name }))} error={errors.clinicId} /></div>
                  <div className="col-span-2 space-y-2 pt-1"><label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block pl-0.5">System Role / Permissions</label><div className="flex flex-wrap gap-1.5">{['doctor', 'nurse', 'receptionist'].map(r => (<button key={r} type="button" onClick={() => setForm({...form, role: r})} className={`flex-1 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all border ${form.role === r ? 'bg-indigo-600 border-indigo-600 text-white shadow-md shadow-indigo-100' : 'bg-slate-50 border-transparent text-slate-500 hover:border-indigo-100'}`}>{r}</button>))}</div></div>
                </div>
              )}
              {currentStep === 3 && (
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 animate-in fade-in duration-300">
                  <InputField label="Corporate Email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="staff@hospital.com" error={errors.email} required /><InputField label="Phone Contact" name="phone" value={form.phone} onChange={handleChange} placeholder="+123..." /><PasswordField label="Setup Password" name="password" value={form.password} onChange={handleChange} show={showPassword} onToggle={() => setShowPassword(!showPassword)} error={errors.password} required={!editingId} /><PasswordField label="Verify Password" name="confirmPassword" value={form.confirmPassword} onChange={handleChange} show={showPassword} onToggle={() => setShowPassword(!showPassword)} error={errors.confirmPassword} required={!editingId} />
                  <div className="col-span-2 flex items-center justify-between p-4 bg-slate-50/50 rounded-2xl border border-dashed border-slate-100 mt-2"><div className="flex items-center gap-3"><div className={`w-8 h-8 rounded-lg flex items-center justify-center ${form.isActive ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-300'}`}><Globe className="w-4 h-4" /></div><div className="text-[10px] font-black text-slate-800 uppercase tracking-widest">Active Presence</div></div><button type="button" onClick={() => setForm({...form, isActive: !form.isActive})} className={`relative inline-flex h-5 w-10 items-center rounded-full transition-all duration-300 ${form.isActive ? 'bg-emerald-50 shadow-md shadow-emerald-100' : 'bg-slate-300'}`}><span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition duration-300 ${form.isActive ? 'translate-x-5.5' : 'translate-x-1'}`} /></button></div>
                </div>
              )}
            </form>
            <div className="px-8 py-5 bg-slate-50/50 flex items-center justify-between border-t border-slate-50">
               <button type="button" onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : setShowModal(false)} className="px-5 py-2 text-slate-400 font-black text-[10px] uppercase tracking-widest hover:text-slate-800 transition-all flex items-center gap-1.5 text-center"><ChevronLeft className="w-4 h-4" />Back</button>
               <button onClick={handleSubmit} className="px-10 py-3 bg-indigo-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center gap-2 hover:bg-black shadow-lg shadow-indigo-100 active:scale-95 transition-all group">{currentStep < 3 ? 'Go to Next' : (editingId ? 'Save Changes' : 'Confirm Setup')}<ChevronRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InputField = ({ label, error, ...props }) => (
  <div className="space-y-1 relative"><div className="flex justify-between items-center px-0.5"><label className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none">{label}</label>{error && <span className="text-[9px] font-black text-red-500 uppercase flex items-center gap-1 animate-pulse"><AlertCircle className="w-3 h-3" /> {error}</span>}</div><div className="relative"><input className={`w-full px-3 py-2 bg-white border rounded-xl text-xs font-bold text-slate-700 focus:outline-none transition-all placeholder:text-slate-200 border-slate-200 shadow-sm ${error ? 'border-red-300 bg-red-50/10 focus:border-red-500 ring-2 ring-red-500/5' : 'focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/5'}`} {...props} /></div></div>
);

const SelectField = ({ label, options, error, ...props }) => (
  <div className="space-y-1 relative"><div className="flex justify-between items-center px-0.5"><label className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none">{label}</label>{error && <span className="text-[9px] font-black text-red-500 uppercase flex items-center gap-1 animate-pulse"><AlertCircle className="w-3 h-3" /> {error}</span>}</div><div className="relative group"><select className={`w-full px-3 py-2 bg-white border rounded-xl text-xs font-bold text-slate-700 focus:outline-none transition-all appearance-none border-slate-200 shadow-sm ${error ? 'border-red-300 bg-red-50/10 focus:border-red-500 ring-2 ring-red-500/5' : 'focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/5'}`} {...props}><option value="">Select Option...</option>{options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}</select><div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-300 group-focus-within:text-indigo-400 transition-colors"><ChevronRight className="w-3 h-3 rotate-90" /></div></div></div>
);

const PasswordField = ({ label, show, onToggle, error, ...props }) => (
  <div className="space-y-1 relative"><div className="flex justify-between items-center px-0.5"><label className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none">{label}</label>{error && <span className="text-[9px] font-black text-red-500 uppercase flex items-center gap-1 animate-pulse"><AlertCircle className="w-3 h-3" /> {error}</span>}</div><div className="relative group"><input type={show ? 'text' : 'password'} className={`w-full px-3 py-2 bg-white border rounded-xl text-xs font-bold text-slate-700 focus:outline-none transition-all placeholder:text-slate-200 border-slate-200 shadow-sm ${error ? 'border-red-300 bg-red-50/10 focus:border-red-500 ring-2 ring-red-500/5' : 'focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/5'}`} {...props} /><button type="button" onClick={onToggle} className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${error ? 'text-red-300' : 'text-slate-300 group-focus-within:text-indigo-500 hover:text-indigo-600'}`}>{show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}</button></div></div>
);

export default PractitionerSetup;
